import React from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface BackButtonProps {
  fallbackPath?: string;
  label?: string;
  variant?: 'button' | 'icon';
  size?: 'small' | 'medium' | 'large';
  tooltip?: string;
}

export const BackButton: React.FC<BackButtonProps> = ({
  fallbackPath,
  label = 'Back',
  variant = 'button',
  size = 'medium',
  tooltip = 'Go back to previous page',
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    // Check if there's browser history to go back to
    if (window.history.length > 1) {
      // Check if we came from within the same app (not external link)
      const referrer = document.referrer;
      const currentOrigin = window.location.origin;
      
      if (referrer.startsWith(currentOrigin) || !referrer) {
        navigate(-1);
        return;
      }
    }

    // Fallback navigation
    if (fallbackPath) {
      navigate(fallbackPath);
    } else {
      // Smart fallback based on current path
      const pathSegments = location.pathname.split('/').filter(Boolean);
      
      if (pathSegments.length >= 2) {
        // Remove the last segment (usually the action like 'show', 'edit')
        const basePath = '/' + pathSegments.slice(0, -1).join('/');
        navigate(basePath);
      } else {
        // Default to dashboard
        navigate('/');
      }
    }
  };

  if (variant === 'icon') {
    return (
      <Tooltip title={tooltip}>
        <IconButton 
          onClick={handleBack}
          size={size}
          sx={{ 
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'action.hover',
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      onClick={handleBack}
      startIcon={<ArrowBackIcon />}
      variant="outlined"
      size={size}
      sx={{
        textTransform: 'none',
        color: 'text.secondary',
        borderColor: 'divider',
        '&:hover': {
          color: 'primary.main',
          borderColor: 'primary.main',
          backgroundColor: 'action.hover',
        }
      }}
    >
      {label}
    </Button>
  );
}; 